import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  CurrencyRupeeIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import MetricCard from './MetricCard';
import QuickActions from './QuickActions';
import TableStatusGrid from './TableStatusGrid';
import CustomerInsights from './CustomerInsights';
import dashboardApiService from '../../services/dashboardApiService';
import toast from 'react-hot-toast';

const COLORS = {
  completed: '#10b981',
  preparing: '#f59e0b',
  ready: '#3b82f6',
  pending: '#f97316',
  cancelled: '#ef4444',
  delivered: '#10b981'
};

const EnhancedOverviewTab = ({ restaurant, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState('week');
  const [revenueData, setRevenueData] = useState(null);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async (showToast = false) => {
    if (!restaurant?.id) return;

    try {
      if (showToast) setRefreshing(true);
      else setLoading(true);

      const response = await dashboardApiService.getAllDashboardData(restaurant.id);
      
      if (response.success) {
        setDashboardData(response.data);
        setRevenueData(response.data.revenueWeek);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [restaurant?.id]);

  // Fetch revenue data for different periods
  const fetchRevenueData = useCallback(async (period) => {
    if (!restaurant?.id) return;

    try {
      const response = await dashboardApiService.getRevenueAnalytics(restaurant.id, period);
      if (response.success) {
        setRevenueData(response.data);
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      toast.error('Failed to load revenue data');
    }
  }, [restaurant?.id]);

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh every 30 seconds for real-time data
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Handle revenue period change
  const handlePeriodChange = (period) => {
    setRevenuePeriod(period);
    fetchRevenueData(period);
  };

  // Handle quick action clicks
  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'live-orders':
        onNavigate?.('orders');
        break;
      case 'add-menu':
        onNavigate?.('menu');
        break;
      case 'qr-code':
        onNavigate?.('tables');
        break;
      case 'staff':
        onNavigate?.('staff');
        break;
      case 'bills':
        onNavigate?.('orders');
        break;
      case 'settings':
        // Handle settings
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl h-40 border border-gray-100 animate-pulse"></div>
          ))}
        </div>
        <div className="bg-white rounded-2xl h-80 border border-gray-100 animate-pulse"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100">
        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
          <ChartBarIcon className="h-8 w-8 text-gray-300" />
        </div>
        <p className="text-gray-900 font-bold text-lg">No data matches your criteria</p>
        <p className="text-gray-500 text-sm mt-1">Check back later once you have some orders</p>
      </div>
    );
  }

  const { metrics, ordersOverview, recentOrders, topItems, staffPerformance, paymentAnalytics } = dashboardData;

  return (
    <div className="space-y-12 pb-16">
      {/* 1. Dynamic Hero Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
        <MetricCard
          title="Day's Revenue"
          value={`₹${parseFloat(metrics?.todayRevenue || 0).toLocaleString('en-IN')}`}
          icon={CurrencyRupeeIcon}
          color="orange"
          comparison={{
            change: metrics?.comparisons?.revenueChange || 0,
            text: `${(metrics?.comparisons?.revenueChange || 0) >= 0 ? 'Surging' : 'Dipping'} vs yesterday`
          }}
          delay={0}
        />

        <MetricCard
          title="Active Kitchen"
          value={metrics?.activeOrders || 0}
          icon={ShoppingBagIcon}
          color="blue"
          subText={`${metrics?.activeOrdersBreakdown?.preparing || 0} Prep • ${metrics?.activeOrdersBreakdown?.ready || 0} Ready`}
          delay={0.1}
        />

        <MetricCard
          title="Total Orders"
          value={metrics?.todayOrders || 0}
          icon={ChartBarIcon}
          color="purple"
          comparison={{
            change: metrics?.comparisons?.ordersChange || 0,
            text: `${Math.abs(metrics?.comparisons?.ordersChange || 0)} ${metrics?.comparisons?.ordersChange > 0 ? 'more' : 'fewer'} today`
          }}
          delay={0.2}
        />

        <MetricCard
          title="Order Accuracy"
          value={`${ordersOverview?.metrics?.acceptanceRate || 0}%`}
          icon={CheckCircleIcon}
          color="green"
          subText={`Avg. Prep: ${ordersOverview?.metrics?.avgPrepTime || 0} mins`}
          delay={0.3}
        />
      </div>

      {/* 2. Rapid Command Center */}
      <QuickActions 
        onActionClick={handleQuickAction}
        activeOrdersCount={metrics?.activeOrders || 0}
      />

      {/* 3. Core Intelligence Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Revenue Pulse */}
        <div className="lg:col-span-2 bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm transition-all hover:shadow-xl hover:shadow-gray-200/20">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-12">
            <div>
              <h3 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <ArrowTrendingUpIcon className="h-6 w-6 text-orange-500" />
                Sales Trends
              </h3>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Live sales overview</p>
            </div>
            <div className="flex bg-gray-50 p-1.5 rounded-2xl border border-gray-100">
              {['today', 'week', 'month'].map((period) => (
                <button
                  key={period}
                  onClick={() => handlePeriodChange(period)}
                  className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-[14px] transition-all ${
                    revenuePeriod === period
                      ? 'bg-white text-gray-900 shadow-lg shadow-gray-200 ring-1 ring-gray-100'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData?.data || []}>
                <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#f9fafb" />
                <XAxis 
                  dataKey="label" 
                  axisLine={false}
                  tickLine={false}
                  fontSize={10}
                  fontWeight={800}
                  tick={{ fill: '#d1d5db' }}
                  dy={15}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  fontSize={10}
                  fontWeight={800}
                  tick={{ fill: '#d1d5db' }}
                  tickFormatter={(val) => `₹${val}`}
                />
                <Tooltip 
                  cursor={{ fill: '#f97316', opacity: 0.03 }}
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    borderRadius: '24px',
                    border: '1px solid #f3f4f6',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                    padding: '16px'
                  }}
                  itemStyle={{ color: '#111827', fontSize: '13px', fontWeight: '900' }}
                  labelStyle={{ color: '#9ca3af', fontSize: '10px', marginBottom: '6px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em' }}
                  formatter={(value) => [`₹${value.toLocaleString()}`, 'NET REVENUE']}
                />
                <Bar 
                  dataKey="revenue" 
                  fill="#f97316" 
                  radius={[12, 12, 0, 0]}
                  barSize={revenuePeriod === 'month' ? 12 : 36}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Breakdown - 1/3 width */}
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm flex flex-col transition-all hover:shadow-xl hover:shadow-gray-200/20">
          <h3 className="text-2xl font-black text-gray-900 tracking-tight">Order Status</h3>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1 mb-10">Real-time order tracking</p>
          
          <div className="flex-1 min-h-[280px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={ordersOverview.statusBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={75}
                  outerRadius={105}
                  paddingAngle={8}
                  dataKey="count"
                >
                  {ordersOverview.statusBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.status] || '#f3f4f6'} stroke="none" />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '24px', 
                    border: '1px solid #f3f4f6', 
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                    padding: '16px'
                  }}
                  itemStyle={{ color: '#111827', fontSize: '12px', fontWeight: '900', textTransform: 'uppercase' }}
                />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Center Label for Donut */}
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none">
              <span className="text-4xl font-black text-gray-900 tracking-tighter">
                {ordersOverview?.statusBreakdown?.reduce((acc, curr) => acc + curr.count, 0) || 0}
              </span>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Orders</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-8">
            {ordersOverview?.statusBreakdown?.map((item) => (
              <div key={item.status} className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50/50 border border-gray-100/50 hover:bg-white hover:border-gray-200 transition-all">
                <div 
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0 shadow-sm" 
                  style={{ backgroundColor: COLORS[item.status] }}
                ></div>
                <div className="min-w-0">
                  <p className="text-[9px] font-black text-gray-400 uppercase truncate mb-0.5">
                    {item.status}
                  </p>
                  <p className="text-sm font-black text-gray-900">
                    {item.count}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 4. Secondary Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Top Performing Items */}
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/20 transition-all">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h3 className="text-2xl font-black text-gray-900 tracking-tight text-orange-500">Best Sellers</h3>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Your most popular dishes</p>
            </div>
            <button 
              onClick={() => onNavigate?.('menu')}
              className="px-6 py-2.5 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-lg shadow-gray-200"
            >
              Manage Menu
            </button>
          </div>
          
          <div className="space-y-6">
            {topItems?.slice(0, 5).map((item, index) => (
              <div key={item.id} className="flex items-center justify-between group">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <img 
                      src={item.image_url || 'https://via.placeholder.com/100'} 
                      alt={item.name}
                      className="w-16 h-16 rounded-[24px] object-cover shadow-lg group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute -top-1 -right-1 w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center shadow-xl border-2 border-white ring-1 ring-orange-100">
                      <span className="text-[10px] font-black">{index + 1}</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 text-base uppercase tracking-tight">
                      {item.name}
                    </h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                      {item.quantitySold} Sold • {item.orderCount} Orders
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-black text-gray-900 text-lg">₹{item.revenue.toLocaleString()}</p>
                  <div className="w-28 bg-gray-50 h-1.5 rounded-full mt-3 overflow-hidden border border-gray-100">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${(item.revenue / (topItems?.[0]?.revenue || 1)) * 100}%` }}
                      className="bg-orange-500 h-full rounded-full"
                      transition={{ delay: 0.5 + index * 0.1, duration: 1 }}
                    />
                  </div>
                </div>
              </div>
            ))}
            {!topItems?.length && (
              <div className="text-center py-10">
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">No elite performance data</p>
              </div>
            )}
          </div>
        </div>

        {/* Staff Leaderboard */}
        <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/20 transition-all">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h3 className="text-2xl font-black text-gray-900 tracking-tight text-blue-600">Staff Team</h3>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Staff performance overview</p>
            </div>
            <button 
              onClick={() => onNavigate?.('staff')}
              className="px-6 py-2.5 bg-gray-50 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-gray-100 transition-all border border-gray-100"
            >
              Manage Staff
            </button>
          </div>

          <div className="grid gap-6">
            {staffPerformance?.staff?.slice(0, 4).map((staff) => (
              <div 
                key={staff.id}
                className="flex items-center justify-between p-6 rounded-[32px] border border-gray-50 bg-gray-50/30 hover:bg-white hover:border-gray-200 hover:shadow-2xl hover:shadow-gray-200/20 transition-all duration-500 group"
              >
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-white ring-4 ring-gray-50 group-hover:ring-blue-50 transition-all">
                      {staff.avatar ? (
                        <img src={staff.avatar} alt={staff.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl font-black text-blue-600 uppercase italic">{staff.name?.charAt(0) || '?'}</span>
                      )}
                    </div>
                    {staff.isAvailable && (
                      <div className="absolute bottom-1 right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white shadow-sm animate-pulse"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 text-sm group-hover:text-blue-600 transition-colors">{staff.name}</h4>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest italic">{staff.position}</span>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-right">
                    <p className="text-[9px] font-black text-gray-300 uppercase tracking-tighter mb-1">Orders</p>
                    <p className="text-base font-black text-gray-900 leading-none">{staff.ordersHandled} <span className="text-[10px] font-bold text-gray-400">Total</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] font-black text-gray-300 uppercase tracking-tighter mb-1">Score</p>
                    <div className="flex items-center text-blue-600">
                      <span className="text-base font-black italic">4.9</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 5. Real-time Status Modules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {dashboardData.tableStatus && (
          <TableStatusGrid 
            tables={dashboardData.tableStatus.tables}
            summary={dashboardData.tableStatus.summary}
          />
        )}
        
        {dashboardData.customerInsights && (
          <CustomerInsights insights={dashboardData.customerInsights} />
        )}
      </div>

      {/* 6. Comprehensive Recent Orders Table */}
      <div className="bg-white rounded-[48px] border border-gray-100 shadow-sm overflow-hidden hover:shadow-2xl hover:shadow-gray-200/20 transition-all duration-700">
        <div className="px-12 py-10 border-b border-gray-50 flex justify-between items-center bg-gray-50/20">
          <div>
            <h3 className="text-2xl font-black text-gray-900 tracking-tight">Recent Orders</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Manage your current orders</p>
          </div>
          <div className="flex items-center gap-4">
            {refreshing && (
              <div className="flex items-center gap-3 px-5 py-2 bg-white border border-gray-100 rounded-full shadow-lg shadow-gray-200 animate-in slide-in-from-right">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Live Updates</span>
              </div>
            )}
            <button
               onClick={() => fetchDashboardData(true)}
               className="p-3 bg-white border border-gray-100 rounded-full hover:bg-gray-50 transition-all shadow-sm group"
            >
              <ArrowTrendingUpIcon className="h-4 w-4 text-gray-400 group-hover:text-orange-500 group-hover:rotate-180 transition-all duration-700" />
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto px-6">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Order ID</th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Customer</th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Total Amount</th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Status</th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Items</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50/50">
              {recentOrders && recentOrders.length > 0 ? recentOrders.map((order, idx) => (
                <tr key={order.id} className="hover:bg-gray-50/30 transition-all group">
                  <td className="px-6 py-8">
                    <p className="text-sm font-black text-gray-900 uppercase tracking-tight group-hover:text-orange-600 transition-colors">#{order.orderNumber || '0000'}</p>
                    <p className="text-[10px] font-bold text-gray-300 uppercase mt-1">
                      {order.createdAt ? new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '00:00'}
                    </p>
                  </td>
                  <td className="px-6 py-8">
                    <div className="flex items-center gap-3">
                       <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center font-black text-gray-400 text-[10px] border border-gray-100">
                          {order.customerName?.charAt(0) || '?'}
                       </div>
                       <div>
                          <p className="text-sm font-black text-gray-900">{order.customerName || 'Customer'}</p>
                          <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tighter">At Table {order.tableNumber || 'N/A'}</span>
                       </div>
                    </div>
                  </td>
                  <td className="px-6 py-8">
                    <p className="text-base font-black text-gray-900">₹{(order.amount || 0).toLocaleString()}</p>
                    <p className="text-[9px] font-black text-emerald-500 uppercase italic">Verified Complete</p>
                  </td>
                  <td className="px-6 py-8">
                    <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                      order.status === 'delivered' || order.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                      order.status === 'preparing' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                      order.status === 'ready' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                      order.status === 'cancelled' ? 'bg-rose-50 text-rose-600 border-rose-100' :
                      'bg-gray-50 text-gray-600 border-gray-100'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-8">
                    <div className="flex items-center gap-4">
                       <div className="flex -space-x-2">
                         {[1,2,3].map(i => (
                           <div key={i} className="w-6 h-6 rounded-full bg-white border-2 border-white shadow-sm ring-1 ring-gray-100 flex items-center justify-center">
                             <div className="w-full h-full rounded-full bg-gray-50 flex items-center justify-center">
                               <span className="text-[8px] grayscale group-hover:grayscale-0 transition-all">🍽️</span>
                             </div>
                           </div>
                         ))}
                       </div>
                       <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest group-hover:text-gray-900 transition-colors cursor-pointer">Details →</span>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="py-32 text-center">
                    <div className="flex flex-col items-center">
                       <div className="w-20 h-20 bg-gray-50 rounded-[24px] flex items-center justify-center mb-6">
                         <ShoppingBagIcon className="h-8 w-8 text-gray-200" />
                       </div>
                       <p className="text-gray-400 font-black uppercase tracking-[0.2em] text-xs">No orders yet</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="px-12 py-8 bg-gray-50/30 border-t border-gray-50 flex justify-between items-center bg-gray-50/20">
           <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest italic">Securely Managed Feed</span>
           <button 
             onClick={() => onNavigate?.('orders')} 
             className="px-6 py-3 bg-white border border-gray-200 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-gray-50 transition-all shadow-sm"
           >
             Access Full History
           </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedOverviewTab;
